<script lang="ts">
  import type { InternalLinkingModule, SanityPicture } from "$lib/types/shared";
  import type { PageBanner } from "$lib/types/page";
  import { contentBlockComponents } from "$lib/portableText/utils";
  import { PortableText } from "@portabletext/svelte";
  import MetaData from "$lib/MetaData.svelte";
  import CoverBlock from "$lib/CoverPicture.svelte";
  import Banner from "./Banner.svelte";
  import FAQ from "$lib/FAQ.svelte";
  import InternalLinking from "$lib/InternalLinking.svelte";
  import CustomerServiceBanner from "$lib/CustomerServiceBanner.svelte";
  import Picture from "$lib/SanityPicture.svelte";
  import { breakpoints as br } from "$lib/utils/getBreakpoints";
  import theTime from "$lib/utils/the-time";

  let { data } = $props();

  let page = $derived(data?.page);
  let seo = $derived(page?.seo);
  let blocks = $derived(page?.pageBuilder || []);
  let topCover = $derived(blocks[0]._type === "cover" ? blocks[0].cover : null);
</script>

<svelte:head>
  {#if seo?.noIndex}
    <meta name="robots" content="noindex" />
  {/if}
</svelte:head>

<MetaData
  data={{
    title: seo?.title || page?.title,
    description: seo?.description || "",
    ...(seo?.image || {}),
  }}
/>

{#if topCover}
  <CoverBlock image={topCover.image}>
    <h1 class="my-0 text-h-hero font-title tablet:leading-tight">
      {topCover.title || page?.title}
    </h1>
  </CoverBlock>
{:else}
  <div class="mx-edge-x laptop:mx-auto max-w-prose">
    <h1>{page?.title}</h1>
  </div>
{/if}

{#if page?.showDate}
  <div class="mx-edge-x laptop:mx-auto max-w-prose">
    <p class="italic text-sm">
      Sist oppdatert {theTime(page?._updatedAt).toLocaleDateString("nb-NO", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })}
    </p>
  </div>
{/if}

{#each blocks as block, i (i)}
  {#if topCover && i === 0}
    <!-- skip already rendered cover block -->
  {:else}
    <!-- render blocks by type -->
    {#if block._type === "content"}
      <div class="mx-edge-x laptop:mx-auto max-w-prose">
        <PortableText
          value={block.content}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
    {/if}

    {#if block._type === "banner"}
      <Banner block={block as PageBanner} />
    {/if}
    {#if block._type === "faqs"}
      <section class="my-12 mx-edge-x">
        <h2>{block.faqs.heading || `Spørsmål og svar`}</h2>
        <div class="border-solid border-3 rounded-2xl p-2">
          {#each block.faqs.items as question (question)}
            <FAQ answer={question} />
          {/each}
        </div>
      </section>
    {/if}

    {#if block._type === "cover"}
      <CoverBlock image={block.cover.image}>
        <h2 class="my-0 text-h-hero font-title tablet:leading-tight">
          {block.cover.title}
        </h2>
      </CoverBlock>
    {/if}

    {#if block._type === "internalLinkingModule"}
      <InternalLinking module={block.module as InternalLinkingModule} />
    {/if}

    {#if block._type === "customerServiceBanner"}
      {#if block.showBanner}
        <CustomerServiceBanner
          title={block.title}
          showMessageInput={block.showMessageInput}
          contactForm={block.showContactForm}
          customPhone={block.customPhone}
          customEmail={block.customEmail}
          customFeedback={block.customFeedback}
        />
      {/if}
    {/if}

    {#if block._type === "gallery"}
      {#if block.images.length === 1}
        {@const image = block.images[0]}
        <div class="mx-edge-x laptop:mx-auto max-w-prose my-8">
          <Picture
            pictureObject={(image as SanityPicture) || {}}
            alt={image?.alt || ""}
            sizes={`(min-width: ${br.desktop}) 85vw, (min-width: ${br.laptop}) 90vw, 100vw`}
            class="h-full w-full object-cover rounded-2xl"
          />
        </div>
      {:else}
        <div
          class="my-8 mx-edge-x laptop:mx-auto flex flex-wrap gap-4 items-center laptop:justify-center"
        >
          {#each block.images as image, i (i)}
            <Picture
              pictureObject={(image as SanityPicture) || {}}
              sizes={`(min-width: ${br.wide}) 20vw, (min-width: ${br.desktop}) 30vw, (min-width: ${br.tablet}) 40vw, 100vw`}
              alt={image?.alt || ""}
              class="rounded-xl object-cover"
            />
          {/each}
        </div>
      {/if}
    {/if}
  {/if}
{/each}
